import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  PORTAL_SEARCH_DEBOUNCE_MS,
  PORTAL_SEARCH_DEBOUNCE_MS_MAX,
  PORTAL_SEARCH_DEBOUNCE_MS_MIN,
} from './portal-search-debounce.constants';

/**
 * Resolved search debounce (system .env default, optionally overridden per user).
 * Populated from GET /auth/contact after login; falls back to {@link PORTAL_SEARCH_DEBOUNCE_MS}.
 */
@Injectable({ providedIn: 'root' })
export class PortalSearchDebounceService {
  private readonly msSubject = new BehaviorSubject<number>(PORTAL_SEARCH_DEBOUNCE_MS);
  private readonly defaultMsSubject = new BehaviorSubject<number>(PORTAL_SEARCH_DEBOUNCE_MS);
  private readonly overrideMsSubject = new BehaviorSubject<number | null>(null);

  readonly ms$: Observable<number> = this.msSubject.asObservable();
  readonly defaultMs$: Observable<number> = this.defaultMsSubject.asObservable();
  readonly overrideMs$: Observable<number | null> = this.overrideMsSubject.asObservable();

  get ms(): number {
    return this.msSubject.value;
  }

  get defaultMs(): number {
    return this.defaultMsSubject.value;
  }

  get overrideMs(): number | null {
    return this.overrideMsSubject.value;
  }

  /** Apply values from /auth/contact (or account settings save). */
  applyFromContact(contact: {
    search_debounce_ms?: number | null;
    search_debounce_ms_default?: number | null;
    search_debounce_ms_override?: number | null;
  } | null | undefined): void {
    if (!contact) {
      return;
    }
    const systemDefault = this.clamp(
      Number(contact.search_debounce_ms_default ?? PORTAL_SEARCH_DEBOUNCE_MS)
    );
    this.defaultMsSubject.next(systemDefault);

    const overrideRaw = contact.search_debounce_ms_override;
    const override =
      overrideRaw === null || overrideRaw === undefined || overrideRaw === ('' as unknown)
        ? null
        : this.clamp(Number(overrideRaw));
    this.overrideMsSubject.next(override);

    const resolved = this.clamp(
      Number(contact.search_debounce_ms ?? override ?? systemDefault)
    );
    this.msSubject.next(resolved);
  }

  /** Reset to compile-time fallback (e.g. on logout). */
  reset(): void {
    this.msSubject.next(PORTAL_SEARCH_DEBOUNCE_MS);
    this.defaultMsSubject.next(PORTAL_SEARCH_DEBOUNCE_MS);
    this.overrideMsSubject.next(null);
  }

  clamp(ms: number): number {
    if (!Number.isFinite(ms)) {
      return PORTAL_SEARCH_DEBOUNCE_MS;
    }
    return Math.min(PORTAL_SEARCH_DEBOUNCE_MS_MAX, Math.max(PORTAL_SEARCH_DEBOUNCE_MS_MIN, Math.round(ms)));
  }
}
